import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useOutletContext, Link } from "react-router-dom";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import {
  SearchAndSort,
  SortOption,
  StatsCardVisual,
  SkeletonCard,
  EmptyState,
  Pagination,
} from "../../features/statsNew/components";
import type { DateRange } from "../../features/statsNew/components";
import { getSessionDate } from "../../features/statsNew/utils/dateUtils";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import {
  getAllSessionsByProjectGroup,
  getSelectedProjectGroupWithSessions,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import {
  fetchAllCurrentElements,
  getCurrentElements,
  IProjectGroupElementsFromCustomAPISourceThumbnail,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { AppDispatch, RootState } from "../../common/state/store";
import { useCreatorStatsCache, useCreatorModulesCache } from "../../features/statsNew/hooks";

interface OutletContextType {
  dateRange: DateRange;
}

const getSessionBadgeVariant = (
  sessionCount: number,
): "sessionLow" | "sessionMedium" | "sessionHigh" => {
  if (sessionCount <= 5) return "sessionLow";
  if (sessionCount <= 50) return "sessionMedium";
  return "sessionHigh";
};

const ParcoursDetailView = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { parcoursId, userId } = useParams<{ parcoursId: string; userId?: string }>();
  const { dateRange } = useOutletContext<OutletContextType>();
  const locale = i18n.language === "fr" ? fr : enUS;

  // Cache hooks
  const { shouldRefetch, markAsFetched } = useCreatorStatsCache();
  const { shouldRefetchModules, markModulesAsFetched } = useCreatorModulesCache(
    parcoursId ? Number(parcoursId) : undefined,
  );

  // Check if we're in learner context
  const isLearnerContext = !!userId;
  const isAnonymousView = userId === "anonymous";

  const parcours = useSelector((state: RootState) =>
    getSelectedProjectGroupWithSessions(Number(parcoursId))(state),
  );
  const projectElements = useSelector(getCurrentElements);
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  // Get learner info when in learner context
  const learnerInfo = useMemo(() => {
    if (!isLearnerContext) return null;

    // For anonymous view, return translated "Anonymous" name
    if (isAnonymousView) {
      return { displayName: t("statsNew.apprenants.anonymous") };
    }

    for (const group of sessionsByProjectGroup) {
      for (const project of group.projects) {
        const session = project.sessions.find((s) => s.enduser?.id === Number(userId));
        if (session?.enduser) {
          const { firstname, lastname, username } = session.enduser;
          const displayName =
            firstname || lastname ? `${firstname || ""} ${lastname || ""}`.trim() : username || "";
          return { displayName };
        }
      }
    }
    return null;
  }, [sessionsByProjectGroup, userId, isLearnerContext, isAnonymousView, t]);

  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState("activity-desc");
  const [isLoading, setIsLoading] = useState(false);
  const [isElementsLoading, setIsElementsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showDeleted, setShowDeleted] = useState(false);

  // Fetch sessions only if cache is invalid
  useEffect(() => {
    if (shouldRefetch) {
      setIsLoading(true);
      dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewParcoursDetailView" })).then(
        () => {
          markAsFetched();
          setIsLoading(false);
        },
      );
    }
  }, [dispatch, shouldRefetch, markAsFetched]);

  // Fetch modules only if cache is invalid (parcoursId changed or visibility refresh)
  useEffect(() => {
    if (parcoursId && shouldRefetchModules) {
      setIsElementsLoading(true);
      dispatch(
        fetchAllCurrentElements({
          componentId: "statsNewParcoursDetailView",
          projectGroupId: Number(parcoursId),
        }),
      ).then(() => {
        markModulesAsFetched();
        setIsElementsLoading(false);
      });
    }
  }, [dispatch, parcoursId, shouldRefetchModules, markModulesAsFetched]);

  // Show loading state
  const showSkeleton =
    isLoading || isElementsLoading || (shouldRefetch && sessionsByProjectGroup.length === 0);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  const sortOptions: SortOption[] = [
    // Par activité
    { key: "activity-desc", label: t("statsNew.sort.activityRecent") },
    { key: "activity-asc", label: t("statsNew.sort.activityOldest") },
    { key: "sessions-desc", label: t("statsNew.sort.sessionsMost") },
    { key: "sessions-asc", label: t("statsNew.sort.sessionsLeast") },
    // Par date
    { key: "firstSession-desc", label: t("statsNew.sort.firstSessionRecent") },
    { key: "firstSession-asc", label: t("statsNew.sort.firstSessionOldest") },
    { key: "created-desc", label: t("statsNew.sort.createdRecent") },
    { key: "created-asc", label: t("statsNew.sort.createdOldest") },
    // Par nom
    { key: "name-asc", label: t("statsNew.sort.nameAZ") },
    { key: "name-desc", label: t("statsNew.sort.nameZA") },
  ];

  const modulesData = useMemo(() => {
    if (!parcours?.projects) return [];

    return parcours.projects.map((project, index) => {
      let sessionCount = 0;
      let lastSessionDate: Date | null = null;
      let firstSessionDate: Date | null = null;

      project.sessions.forEach((session) => {
        // Filter by user in learner context
        if (isLearnerContext) {
          if (isAnonymousView) {
            // For anonymous view, only include sessions without enduser
            if (session.enduser) return;
          } else {
            // For specific user view, filter by user ID
            if (session.enduser?.id !== Number(userId)) return;
          }
        }

        const sessionDate = getSessionDate(session);

        // Apply date range filter
        if (dateRange.startDate && sessionDate < dateRange.startDate) return;
        if (dateRange.endDate && sessionDate > dateRange.endDate) return;

        sessionCount++;
        if (!lastSessionDate || sessionDate > lastSessionDate) {
          lastSessionDate = sessionDate;
        }
        if (!firstSessionDate || sessionDate < firstSessionDate) {
          firstSessionDate = sessionDate;
        }
      });

      // Find the project element for thumbnail and createdAt
      const element = projectElements.find((el) => el.project?.id === project.project_id);
      // Cast source_thumbnail since API populates it as object, not number
      const thumbnail = element?.project?.source_thumbnail as unknown as
        | IProjectGroupElementsFromCustomAPISourceThumbnail
        | undefined;
      const thumbnailUrl = thumbnail?.s3_url || null;
      const createdAt = element?.project?.created_at ? new Date(element.project.created_at) : null;

      return {
        id: project.project_id,
        name: project.project_name,
        sessionCount,
        lastSessionDate,
        firstSessionDate,
        createdAt,
        order: index,
        isDeleted: project.is_deleted,
        thumbnailUrl,
      };
    });
  }, [parcours, dateRange, projectElements, isLearnerContext, isAnonymousView, userId]);

  const filteredAndSortedData = useMemo(() => {
    // Filter out modules with 0 sessions in the date range
    let result = modulesData.filter((m) => m.sessionCount > 0);

    // Filter out deleted modules if showDeleted is false
    if (!showDeleted) {
      result = result.filter((m) => !m.isDeleted);
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((m) => m.name.toLowerCase().includes(search));
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "sessions-desc":
          return b.sessionCount - a.sessionCount;
        case "sessions-asc":
          return a.sessionCount - b.sessionCount;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "activity-desc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return b.lastSessionDate.getTime() - a.lastSessionDate.getTime();
        case "activity-asc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return a.lastSessionDate.getTime() - b.lastSessionDate.getTime();
        case "firstSession-desc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return b.firstSessionDate.getTime() - a.firstSessionDate.getTime();
        case "firstSession-asc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return a.firstSessionDate.getTime() - b.firstSessionDate.getTime();
        case "created-desc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt.getTime() - a.createdAt.getTime();
        case "created-asc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return a.createdAt.getTime() - b.createdAt.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [modulesData, searchValue, sortKey, showDeleted]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortKey, dateRange, showDeleted]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleModuleClick = (moduleId: number) => {
    if (isLearnerContext) {
      navigate(`/statistics/apprenants/${userId}/${parcoursId}/${moduleId}`);
    } else {
      navigate(`/statistics/parcours/${parcoursId}/${moduleId}`);
    }
  };

  const formatLastSession = (date: Date | null): string => {
    if (!date) return t("statsNew.module.noSessions");
    return `${t("statsNew.module.lastSession")}: ${format(date, "dd MMM yyyy", { locale })}`;
  };

  // Show skeleton during initial load
  if (showSkeleton && !parcours?.project_group_id) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Header skeleton */}
        <div>
          <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Search skeleton */}
        <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse" />

        {/* Cards skeleton */}
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="module" />
          ))}
        </div>
      </div>
    );
  }

  if (!parcours?.project_group_id) {
    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.parcours.notFound")}
        description={t("statsNew.emptyState.noParcoursDescription")}
      />
    );
  }

  // Render empty state based on context
  const renderEmptyState = () => {
    if (searchValue) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }

    if (dateRange.startDate || dateRange.endDate) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.emptyState.noModules")}
          description={t("statsNew.emptyState.noModulesDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: handleClearDateRange,
          }}
        />
      );
    }

    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.emptyState.noModules")}
        description={t("statsNew.emptyState.noModulesDescription")}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-4">
        {isLearnerContext ? (
          <>
            <Link to="/statistics/apprenants">{t("statsNew.tabs.apprenants")}</Link>
            <span className="stats-breadcrumb-separator">/</span>
            <Link to={`/statistics/apprenants/${userId}`}>
              {learnerInfo?.displayName || userId}
            </Link>
            <span className="stats-breadcrumb-separator">/</span>
            <span className="stats-breadcrumb-current">{parcours.project_group_name}</span>
          </>
        ) : (
          <>
            <Link to="/statistics/parcours">{t("statsNew.tabs.parcours")}</Link>
            <span className="stats-breadcrumb-separator">/</span>
            <span className="stats-breadcrumb-current">{parcours.project_group_name}</span>
          </>
        )}
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{parcours.project_group_name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {modulesData.filter((m) => m.sessionCount > 0).length} {t("statsNew.parcours.modules")}
          </p>
        </div>
      </div>

      {/* Search, Sort and Filters */}
      <div className="flex-shrink-0 mb-4 flex items-center gap-4">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.module.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={setSortKey}
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
          />
          {t("statsNew.filters.showDeleted")}
        </label>
      </div>

      {/* Modules Grid */}
      {isElementsLoading ? (
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: parcours.projects?.length || 4 }).map((_, i) => (
            <SkeletonCard key={i} variant="module" />
          ))}
        </div>
      ) : filteredAndSortedData.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="flex flex-wrap gap-4">
              {paginatedData.map((module) => (
                <StatsCardVisual
                  key={module.id}
                  title={module.name}
                  subtitle={formatLastSession(module.lastSessionDate)}
                  thumbnailUrl={module.thumbnailUrl || undefined}
                  badge={
                    module.sessionCount > 0
                      ? {
                          text: `${module.sessionCount} ${t("statsNew.session.sessions")}`,
                          variant: getSessionBadgeVariant(module.sessionCount),
                        }
                      : {
                          text: t("statsNew.module.noSessions"),
                          variant: "neutral",
                        }
                  }
                  onClick={() => handleModuleClick(module.id)}
                  compact
                  footer={
                    module.isDeleted ? (
                      <span className="text-xs text-red-500">{t("statsNew.module.deleted")}</span>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 mt-auto">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAndSortedData.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ParcoursDetailView;
